import { useState } from "react";

import { PageHeader } from "@/design-system/components/ui/PageHeader";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DetailField,
  DetailFieldList,
  FilterChips,
  Input,
  Label,
  RECORD_CARD_MIN_WIDTH,
  RECORD_RAIL_WIDTH,
  ResponsiveGrid,
  ResponsiveSplit,
  ScopeToggle,
  SearchField,
  StatCard,
  StatsBar,
  StatusBadge,
  StatusStepper,
  Textarea,
  Timestamp,
  SubTable,
  Surface,
  TableValue,
  Tooltip,
  Truncate,
  type SubTableColumn,
} from "@/design-system/components/ui";
import tokens from "@/design-system/tokens.json";

/**
 * THE STYLE GUIDE. Internal, `System.Configure`-gated, no new permission.
 *
 * 🔴 IT IMPORTS THE REAL COMPONENTS. Not copies, not examples, not a snapshot
 * of what they looked like when this was written. A guide built from
 * hand-written imitations drifts from the code exactly the way the prose in the
 * two standards drifted from the implementation — which is the failure this
 * page exists to stop, so reproducing it here would be a joke at its own
 * expense. If a primitive changes, this page changes with it, because it is
 * rendering that primitive.
 *
 * 🔴 AND IT IS NOT THE ENFORCEMENT. A page that renders components proves
 * nothing about whether anything obeys them — it cannot fail. The enforcing
 * mechanisms are:
 *   · `src/test/typeScaleResolves.test.ts` — every `text-*` class must resolve
 *     to a real token; shrink-only baselines; verified by breaking a component
 *     and watching it go red.
 *   · `src/design-system/lib/utils.test.ts` — `cn` must keep a size and a
 *     colour together in either order; verified the same way.
 *   · `eslint.config.js` — the patterns the standards replace.
 * This page is the readable half. Do not mistake it for the gate.
 *
 * The specifications are `docs/specs/core/Phoenix-Capital-Table-and-Tile-Standard-v1.0.md`
 * (§16 card anatomy, §17 section headings, §18 button hierarchy) and
 * `…-Entity-Detail-Page-Layout-Standard-v1.1.md`. This page shows; those
 * documents rule.
 */

/** Renders a specimen next to the exact class string that produced it. */
function Specimen({
  name,
  classes,
  note,
  children,
}: {
  name: string;
  classes?: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border-light py-space-3 last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-space-3 mb-space-2">
        <span className="text-label uppercase tracking-label text-gray-mid">{name}</span>
        {classes ? (
          <code className="text-caption text-gold-shadow font-mono break-all">{classes}</code>
        ) : null}
      </div>
      <div className="mb-space-2">{children}</div>
      {note ? <p className="text-caption text-gray-mid m-0 max-w-reading">{note}</p> : null}
    </div>
  );
}

function Section({ id, title, blurb, children }: { id: string; title: string; blurb: string; children: React.ReactNode }) {
  return (
    <Card className="mb-space-5" data-testid={`style-${id}`}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{blurb}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/** A specimen row, carrying one of each thing a cell is allowed to be. */
type GuideRow = {
  id: string;
  name: string;
  category: string;
  /** A state that is PRESENT, ABSENT, or an exception worth marking. */
  state: "present" | "absent" | "exception";
  amount: number | null;
};

const GUIDE_ROWS: GuideRow[] = [
  { id: "g1", name: "Northwind Plumbing", category: "Services", state: "present", amount: 1_250_000 },
  { id: "g2", name: "Cedar Valley Dental", category: "Healthcare", state: "absent", amount: 890_000 },
  { id: "g3", name: "Harbor Freight Logistics", category: "Transport", state: "exception", amount: null },
  { id: "g4", name: "Sunbelt HVAC", category: "Services", state: "present", amount: 2_100_000 },
];

/** Twelve rows: past `SUB_TABLE_CHROME_THRESHOLD`, so the chrome appears. */
const MANY_GUIDE_ROWS: GuideRow[] = Array.from({ length: 12 }, (_, i) => ({
  ...GUIDE_ROWS[i % GUIDE_ROWS.length],
  id: `m${i}`,
  name: `${GUIDE_ROWS[i % GUIDE_ROWS.length].name} ${i + 1}`,
}));

const GUIDE_COLUMNS: SubTableColumn<GuideRow>[] = [
  {
    key: "name",
    header: "Business",
    sortAccessor: (r) => r.name,
    render: (r) => (
      <span className="inline-flex items-center gap-space-2">
        <Truncate>{r.name}</Truncate>
        {/* §6.3 — inline after the row's own name, never its own column. */}
        {r.state === "exception" && <StatusBadge tone="warn">Needs review</StatusBadge>}
      </span>
    ),
  },
  {
    key: "category",
    header: "Category",
    render: (r) => <StatusBadge tone="onyx">{r.category}</StatusBadge>,
  },
  {
    key: "state",
    header: "NDA",
    searchText: (r) => (r.state === "present" ? "On file" : "Not requested"),
    render: (r) =>
      r.state === "present" ? (
        <StatusBadge tone="ok">On file</StatusBadge>
      ) : (
        // §6.1 — a state that is ABSENT is muted prose. A grey pill reads as a
        // status somebody set, and nobody set this one.
        <span className="text-caption text-gray-mid">Not requested</span>
      ),
  },
  {
    key: "amount",
    header: "Asking",
    align: "right",
    sortAccessor: (r) => r.amount,
    render: (r) => (
      <TableValue value={r.amount == null ? null : `$${r.amount.toLocaleString()}`} />
    ),
  },
];

const BUTTON_VARIANTS = ["primary", "secondary", "tertiary", "text", "onDark", "destructive", "icon"] as const;
const TONES = ["neutral", "progress", "info", "warn", "ok", "danger", "muted", "onyx"] as const;

export function StyleGuidePage() {
  const [scope, setScope] = useState<"mine" | "all">("mine");
  const [chip, setChip] = useState("all");
  const [q, setQ] = useState("");

  return (
    <div>
      <PageHeader
        eyebrow="Internal"
        title="Style guide"
        breadcrumbs={[{ label: "Admin", to: "/admin" }, { label: "Style guide" }]}
      />

      <p className="text-body text-gray-mid max-w-reading mb-space-5">
        Every specimen below is the real component, rendered live, with the class string that
        produced it. If something here looks wrong, the component is wrong — not this page.
        The rules live in the Table &amp; Tile Standard and the Entity Detail Page Layout
        Standard; this page is how you check the code still matches them.
      </p>

      {/* ---------------------------------------------------------------- */}
      <Section
        id="type"
        title="Type scale"
        blurb="Nine steps, from tokens.json. There is no h4 or h5 — see the note."
      >
        <div className="grid gap-space-2">
          {Object.entries(tokens.typography.fontSize).map(([name, size]) => (
            <div key={name} className="flex flex-wrap items-baseline gap-space-3">
              <code className="text-caption text-gold-shadow font-mono w-32 shrink-0">text-{name}</code>
              <span className="text-caption text-gray-mid w-20 shrink-0 tabular-nums">{size}</span>
              <span className={`text-${name} text-onyx truncate`}>The quick brown fox</span>
            </div>
          ))}
        </div>
        <p className="text-caption text-gray-mid mt-space-3 max-w-reading">
          🔴 <strong>There is no <code>text-h4</code> and no <code>text-h5</code>.</strong> Both are
          written across 147 files and neither resolves to anything — Tailwind emits no rule, so
          the element silently inherits its parent&rsquo;s size. That is why section headings have
          been ad-hoc. <code>src/test/typeScaleResolves.test.ts</code> holds the line at today&rsquo;s
          count and will not let it grow; adding an <code>h4</code> step is a design decision for
          <code> tokens.json</code>, not a find-and-replace.
        </p>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section
        id="buttons"
        title="Button hierarchy"
        blurb="Table & Tile Standard §18. One gold primary per surface; everything else is secondary, text or in an overflow."
      >
        <Specimen
          name="All variants"
          classes='<Button variant="…" size="md">'
          note="Every one of these lost its declared colour until this PR: cn() classified the custom size as a text colour and the later class won. destructive is written white-on-red and rendered dark-on-red; tertiary and text are written gold-shadow and rendered plain black, indistinguishable from body copy. A vocabulary nobody can see is not a vocabulary."
        >
          <div className="flex flex-wrap items-center gap-space-2">
            {BUTTON_VARIANTS.map((v) => (
              <Button key={v} variant={v}>
                {v}
              </Button>
            ))}
          </div>
        </Specimen>
        <Specimen
          name="On a dark surface"
          classes='variant="onDark" | variant="destructive"'
        >
          <div className="bg-onyx p-space-3 rounded-md flex gap-space-2">
            <Button variant="onDark">On dark</Button>
            <Button variant="destructive">Destructive</Button>
          </div>
        </Specimen>
        <Specimen
          name="Sizes"
          classes='size="sm" | "md" | "lg" | "icon"'
          note="Omitting variant defaults to primary and omitting size defaults to md — so a button with no props is a gold primary chosen by accident. 445 of 2,139 non-test <Button> tags set no variant."
        >
          <div className="flex flex-wrap items-center gap-space-2">
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
          </div>
        </Specimen>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section
        id="badges"
        title="Badge vocabulary"
        blurb="Table & Tile Standard §6. A badge means something — it is not emphasis."
      >
        <Specimen name="Tones" classes='<StatusBadge tone="…">'>
          <div className="flex flex-wrap gap-space-2">
            {TONES.map((t) => (
              <StatusBadge key={t} tone={t}>
                {t}
              </StatusBadge>
            ))}
          </div>
        </Specimen>
        <Specimen
          name="Category"
          classes='<Badge status="gold"> | <StatusBadge tone="onyx">'
          note="What kind of thing this row is."
        >
          <div className="flex flex-wrap gap-space-2">
            <Badge status="gold">Primary</Badge>
            <StatusBadge tone="onyx">Seller</StatusBadge>
          </div>
        </Specimen>
        <Specimen
          name="State absent — prose, never a pill"
          classes='<span className="text-gray-mid italic">'
          note="A pill is a visual interrupt. Rendering 'Not started' as a pill on every row of a young list makes an ordinary book look like a wall of problems, and by the time a real exception appears the reader has stopped seeing pills."
        >
          <span className="text-gray-mid italic">Unassigned</span>
        </Specimen>
        <Specimen
          name="Plain missing value vs actionable absence"
          classes='<span className="text-gray-mid">—</span>'
          note="§7, and it is written once so no table relitigates it: a plain missing value renders — with no action. A workflow state someone could act on carries its own door. The test is whether a person could do something about it."
        >
          <div className="flex flex-wrap items-center gap-space-4">
            <span className="text-gray-mid">—</span>
            <span className="flex items-center gap-space-2">
              <span className="text-gray-mid italic">No portal account</span>
              <Button variant="text" size="sm">
                Invite
              </Button>
            </span>
          </div>
        </Specimen>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section
        id="cards"
        title="Card anatomy"
        blurb="Table & Tile Standard §16. Title 28px, description 14px, content 16px."
      >
        <Specimen name="Card" classes="border border-border-light rounded-md p-space-4 bg-white-pure">
          <Card>
            <CardHeader>
              <CardTitle>Portfolio composition</CardTitle>
              <CardDescription>Segment mix across the contributed book.</CardDescription>
            </CardHeader>
            <CardContent>Body copy sits at text-body and reads below the title.</CardContent>
          </Card>
        </Specimen>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section
        id="field-row"
        title="Record field row"
        blurb="§11.5 — the label/value row on a record card, and §12.1's split. Both specimens are the REAL components at a stated width, so the stack below 22rem is visible rather than described."
      >
        <Specimen
          name="DetailFieldList + DetailField"
          classes="grid-cols-[fit-content(13rem)_minmax(0,1fr)] · gap-x-space-3 · grid-cols-subgrid rows"
          note="The label track is content-sized with a 13rem bound and lives on the LIST, so a card's labels share one left edge and size to the widest label that card carries — 151.23px here, set by CREDIT SCORE BAND, against 202.95px on a card holding DOWN PAYMENT AVAILABLE and the 176px the fixed track spent on every row regardless. The value carries min-w-0 and clamps at two lines. INDUSTRY EXPERIENCE is over its clamp, so it carries an ellipsis and is a tab stop; every other value here fits and is neither, because the reveal attaches only once a value is MEASURED to overflow. break-words is what lets the email wrap — a single unbreakable token gives line-clamp no following line to elide, and the clamp degrades to a silent cut."
        >
          <div className="flex flex-wrap gap-space-4">
            <div style={{ width: 528 }} className="border border-border-light rounded-md p-space-4 bg-white-pure">
              <div className="text-label uppercase tracking-label text-gray-mid mb-space-2">528px — side by side</div>
              <DetailFieldList>
                <DetailField label="Consultant" value="Dana Whitfield" />
                <DetailField label="Email" value="d.whitfield+prequalification@northbridge.example" />
                <DetailField label="Phone" value="(602) 555-0143" />
                <DetailField label="Credit score band" value={null} />
                <DetailField
                  label="Industry experience"
                  wide
                  value="Twenty-two years running commercial print operations across three states, latterly as VP Operations for a forty-press group; wants a services business with recurring contracts, an owner willing to stay through a transition, and no retail footprint."
                />
              </DetailFieldList>
            </div>
            <div style={{ width: 320 }} className="border border-border-light rounded-md p-space-4 bg-white-pure">
              <div className="text-label uppercase tracking-label text-gray-mid mb-space-2">320px — stacked</div>
              <DetailFieldList>
                <DetailField label="Consultant" value="Dana Whitfield" />
                <DetailField label="Email" value="d.whitfield+prequalification@northbridge.example" />
                <DetailField label="Phone" value="(602) 555-0143" />
                <DetailField label="Credit score band" value={null} />
              </DetailFieldList>
            </div>
          </div>
        </Specimen>

        <Specimen
          name="ResponsiveSplit"
          classes={`mainMinWidth="34rem" · railWidth={RECORD_RAIL_WIDTH} · railGrows={false}`}
          note="The record Overview's own shape, and the first thing that ever rendered this primitive. railGrows={false} holds the rail at its stated width while the main column takes every spare pixel — with the default the two split leftover space 2:1, which gave a milestone ladder 533px of a 1536px page. The rail still goes full width when it wraps below the main column."
        >
          {/* 1000px: past the 920px at which this split wraps (34rem main +
              22rem rail + a 24px gap), so the two columns are visible rather
              than described. Below it the rail goes full width under the main. */}
          <div style={{ width: 1000 }} className="max-w-full">
            <ResponsiveSplit
              gap="md"
              mainMinWidth="34rem"
              railWidth={RECORD_RAIL_WIDTH}
              railGrows={false}
              main={
                <ResponsiveGrid cols={2} minColWidth={RECORD_CARD_MIN_WIDTH} gap="md">
                  <div className="border border-border-light rounded-md p-space-4 bg-white-pure text-small">Card</div>
                  <div className="border border-border-light rounded-md p-space-4 bg-white-pure text-small">Card</div>
                </ResponsiveGrid>
              }
              rail={
                <div className="border border-border-light rounded-md p-space-4 bg-white-warm text-small">
                  Rail — holds {RECORD_RAIL_WIDTH}
                </div>
              }
            />
          </div>
        </Specimen>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section id="filters" title="Filters and scope" blurb="§9 — chips, not dropdowns. §9.2 — the mine-vs-everyone toggle.">
        <Specimen
          name="Chips"
          classes="<FilterChips chips={…} activeId onChange />"
          note="A dropdown hides its options until clicked, so the reader cannot see what the list can be filtered by without opening it. Chips show the vocabulary and the counts at rest."
        >
          <FilterChips
            label="Status"
            activeId={chip}
            onChange={setChip}
            chips={[
              { id: "all", label: "All", count: 128 },
              { id: "live", label: "Live", count: 41 },
              { id: "closed", label: "Closed", count: 87 },
            ]}
          />
        </Specimen>
        <Specimen name="Search" classes="<SearchField value onChange placeholder />">
          <SearchField value={q} onChange={setQ} placeholder="Search name, email, phone…" />
        </Specimen>
        <Specimen
          name="Scope"
          classes="<ScopeToggle value onChange mineLabel allLabel />"
          note="Until this PR this primitive had ZERO call sites — built one round ago for a conversion that never happened, and therefore invisible. The buyers index carried the same capability buried as an 'Everyone' option inside a select of staff names."
        >
          <ScopeToggle
            label="Buyer view"
            value={scope}
            onChange={setScope}
            mineLabel="Mine"
            allLabel="Everyone"
          />
        </Specimen>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section id="forms" title="Form controls" blurb="Label is 12px uppercase; Input is 16px on mobile and 14px from sm up.">
        <div className="grid gap-space-3 max-w-[460px]">
          <div>
            <Label htmlFor="sg-a">Business name</Label>
            <Input id="sg-a" defaultValue="Cascade Plumbing & Heating" />
          </div>
          <div>
            <Label htmlFor="sg-b">Notes</Label>
            <Textarea id="sg-b" rows={2} defaultValue="Two rows of body copy." />
          </div>
        </div>
        <p className="text-caption text-gray-mid mt-space-3 max-w-reading">
          The 16px below <code>sm</code> is deliberate and is a fix, not a style: iOS zooms the
          viewport when a focused input is under 16px. Until this PR the base size was stripped by
          <code> cn()</code>, so mobile inherited instead.
        </p>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section id="misc" title="Timestamps, steppers, tooltips, truncation" blurb="§11 — the truncation contract.">
        <Specimen name="Timestamp" classes="<Timestamp value />">
          <Timestamp value="2026-06-12T09:30:00Z" />
        </Specimen>
        <Specimen name="StatusStepper" classes="<StatusStepper steps currentIndex />">
          <StatusStepper
            currentIndex={1}
            steps={[
              { key: "invited", label: "Invited" },
              { key: "active", label: "Active" },
            ]}
          />
        </Specimen>
        <Specimen
          name="Truncate + Tooltip"
          classes="<Truncate lines={1}> · <Tooltip label>"
          note="§11.4 — never the native title attribute. It does not open on keyboard focus, so a keyboard user can reach the value and still be told nothing about it; it also cannot be styled, positioned or dismissed."
        >
          <div className="max-w-[240px]">
            <Truncate lines={1}>
              A considerably longer value than the column it has been given can show
            </Truncate>
          </div>
          <span className="mt-space-2 inline-block">
            <Tooltip label="The full value, on hover and on keyboard focus.">
              <span className="text-small underline decoration-dotted">hover or focus me</span>
            </Tooltip>
          </span>
        </Specimen>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section id="stats" title="Counter strip" blurb="§12 — layout responds to its container, never the viewport.">
        <StatsBar columns={6}>
          <StatCard label="Total" value={128} />
          <StatCard label="Qualified" value={41} />
          <StatCard label="Presented" value={22} />
          <StatCard label="Under offer" value={9} />
          <StatCard label="Closed" value={4} />
          <StatCard label="Verdict" value={<StatusBadge tone="ok">Pass</StatusBadge>} />
        </StatsBar>
        <p className="text-caption text-gray-mid mt-space-3 max-w-reading">
          Narrow the window: the bar sheds columns on the width it has been given, not at a
          viewport breakpoint. Rendered in a drawer or a split pane the old version kept a
          page-width six-across grid inside a box nowhere near page width.
        </p>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section
        id="tables"
        title="Tables"
        blurb="§4.3 header variant · §5 progressive chrome · §6 badge vocabulary · §11 truncation."
      >
        <div className="grid gap-space-5">
          <div>
            <p className="text-caption text-gray-mid mb-space-2 max-w-reading">
              🔴 <strong>The header variant is not an author's choice.</strong> Both of these
              render the SAME component with the same props. The only difference is the box
              they are standing in — the one below is inside a <code>Surface kind="card"</code>,
              which is what a record's section or a drawer publishes. Onyx is the page's own
              furniture; inside a card it reads as a second page glued into the first.
            </p>
            <Surface kind="page">
              <SubTable<GuideRow>
                caption="Page-level table"
                rows={GUIDE_ROWS}
                rowKey={(r) => r.id}
                columns={GUIDE_COLUMNS}
              />
            </Surface>
          </div>

          <div>
            <Surface kind="card">
              <SubTable<GuideRow>
                caption="The same table inside a card"
                rows={GUIDE_ROWS}
                rowKey={(r) => r.id}
                columns={GUIDE_COLUMNS}
              />
            </Surface>
            <p className="text-caption text-gray-mid mt-space-2 max-w-reading">
              The rows show the rest of the vocabulary at the same time. A category is a
              pill; a state that is PRESENT is a pill; a state that is ABSENT is muted prose
              and never a pill, because a grey pill reads as a status somebody set. An
              exception rides inline after the row's own name rather than taking a column
              that would be empty on every ordinary row. A plain missing value is an em dash
              with no action — <code>TableValue</code> owns that rule so it is written once.
            </p>
          </div>

          <div>
            <p className="text-caption text-gray-mid mb-space-2 max-w-reading">
              🔴 <strong>Chrome arrives with the content that needs it.</strong> The table
              above has four rows and no search box, no sort arrows and no footer. This one
              is identical except that it has twelve, so all three appear together. Nobody
              decided that per table: <code>SubTable</code> reads the row count. A search
              field over six rows is furniture that answers a question the reader could
              answer faster by looking.
            </p>
            <Surface kind="card">
              <SubTable<GuideRow>
                caption="The same table, past the threshold"
                rows={MANY_GUIDE_ROWS}
                rowKey={(r) => r.id}
                columns={GUIDE_COLUMNS}
              />
            </Surface>
            <p className="text-caption text-gray-mid mt-space-2 max-w-reading">
              ⚠️ <strong>Chrome that implies scale must be backed by whatever holds the
              data.</strong> These controls act on the array the table was handed, which is
              honest here because that array is everything there is. A table whose endpoint
              pages must NOT use them — a search box filtering one page of twenty-five
              claims to search a book it cannot see. Give <code>SubTable</code> a
              <code>chromeThreshold</code> of the page size so its own chrome can never
              activate, and let the page own the controls server-side.
            </p>
          </div>
        </div>
      </Section>

      {/* ---------------------------------------------------------------- */}
      <Section
        id="patterns"
        title="Named patterns"
        blurb="Shapes that exist in several places and have drifted. Naming one is how the next copy stays a copy."
      >
        <div className="grid gap-space-4 max-w-reading">
          <div>
            <p className="text-small font-semibold text-onyx m-0 mb-space-1">
              Paste &rarr; extract &rarr; review &rarr; save
            </p>
            <p className="text-caption text-gray-mid m-0">
              Reference implementation: <code>PasteToCreateListingModal</code>. Every extracted
              value lands in an ordinary <em>editable</em> input, so the review surface IS the
              extraction — not a summary the reader accepts blind. <code>open</code> is a
              controlled prop so the host owns the lifecycle. <code>onCreated</code> hands back the
              full record, not an id and not nothing. One <code>reset()</code> clears every phase.
              The 422 fallback renders the server&rsquo;s message and preserves the paste. The model
              identifier is never published. Seven implementations of this shape exist and they
              have drifted; a new one copies that file.
            </p>
          </div>
          <div>
            <p className="text-small font-semibold text-onyx m-0 mb-space-1">
              Per-section edit
            </p>
            <p className="text-caption text-gray-mid m-0">
              One config drives both the read card and the edit modal, with keys constrained to
              <code> keyof UpdatePayload ∩ keyof DetailDto</code> so a rename fails typecheck
              instead of silently editing nothing. The pencil goes in{" "}
              <code>DetailSection</code>&rsquo;s <code>action</code> slot, on the card that displays
              the data — Layout Standard §4.1a.1: the Overview rule governs workflow actions, and
              correcting a field on the record you are looking at is data maintenance.
            </p>
          </div>
          <div>
            <p className="text-small font-semibold text-onyx m-0 mb-space-1">
              Resolver + parity fixture
            </p>
            <p className="text-caption text-gray-mid m-0">
              Any fact that crosses the C#/TS boundary gets one resolver each side and a shared
              JSON fixture both suites read — never a keep-in-sync comment. Six ladders for the
              listing display name existed before this was named, and two of the deleted
              duplicates were named after a field they did not actually read.
            </p>
          </div>

          <div>
            <p className="text-small font-semibold text-onyx m-0 mb-space-1">
              Conditional block, keyed on the data
            </p>
            <p className="text-caption text-gray-mid m-0 mb-space-3">
              Reference implementation: the submission block in{" "}
              <code>LogCommunicationModal</code>. A form that asks extra questions only
              when the record in hand warrants them. The rule that makes it durable:{" "}
              <strong>key the condition on the DATA, never on the mount point</strong>.
              That modal is opened by the central inbox and by twenty entity tab panels,
              so &ldquo;am I inside the submission drawer&rdquo; would have left the block
              missing from the surface most people use. Ask{" "}
              <code>does this entry carry a buyer_submission placement</code> instead, and
              every mount gets it for free.
            </p>
            <p className="text-caption text-gray-mid m-0 mb-space-3">
              Two questions can belong to one block and still be stored at different
              levels. Eligibility renders per placement ROW; the counterparty renders
              once for the ENTRY. Asked together, stored apart — because who you spoke to
              cannot differ between two filings of one conversation, and whether it may
              be shared can.
            </p>
            <p className="text-caption text-gray-mid m-0 mb-space-2">
              Chrome, in copyable class names: the group is a bare{" "}
              <code>&lt;fieldset className=&quot;m-0 border-0 p-0&quot;&gt;</code> with a{" "}
              <code>&lt;legend&gt;</code> at{" "}
              <code>text-label text-gray-mid uppercase tracking-label</code>; each option
              is a <code>&lt;label className=&quot;flex items-start gap-space-2
              text-small text-onyx&quot;&gt;</code> whose radio takes{" "}
              <code>mt-[3px]</code> to sit on the first line of text; the explanatory line
              under each option is <code>block text-caption text-gray-mid</code>; and a
              consequence warning is <code>text-caption text-status-warning</code>. Indent
              the block from its parent row with <code>pl-space-3</code>, not a margin.
            </p>
            <fieldset
              className="m-0 border-0 p-0 pl-space-3 flex flex-col gap-space-1"
              data-testid="sg-conditional-block"
            >
              <legend className="text-label text-gray-mid uppercase tracking-label mb-space-1">
                Who can see this
              </legend>
              <label className="flex items-start gap-space-2 text-small text-onyx">
                <input type="radio" name="sg-conditional" defaultChecked className="mt-[3px]" />
                <span>
                  <span className="block">Internal only</span>
                  <span className="block text-caption text-gray-mid">
                    Phoenix staff only. Never leaves the building.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-space-2 text-small text-onyx">
                <input type="radio" name="sg-conditional" className="mt-[3px]" />
                <span>
                  <span className="block">May appear in a package</span>
                  <span className="block text-caption text-gray-mid">
                    Can be used to write the package sent to this listing&rsquo;s broker.
                  </span>
                </span>
              </label>
              <p className="text-caption text-status-warning m-0">
                This note can be read by someone outside Phoenix. Write it for that
                audience.
              </p>
            </fieldset>
            <p className="text-caption text-gray-mid m-0 mt-space-2">
              The copy is the other half of the pattern. Each option states what the
              system DOES with the choice, in the reader&rsquo;s words rather than the
              schema&rsquo;s — and the consequence line appears only once the consequential
              option is selected, so it reads as a warning rather than as decoration.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}
